import React, { useContext } from 'react';
import { Link as GatsbyLink, graphql, useStaticQuery } from 'gatsby';
import { Box, AspectRatio, Heading, Hide, HStack } from '@chakra-ui/react';
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import SliderArrows from '@components/molecules/SliderArrows';
import SliderPagination from '@components/molecules/SliderPagination';
import Section from '@components/atoms/Section';
import Wrapper from '@components/atoms/Wrapper';
import Image from '@components/atoms/Image';
import { LocaleContext } from '@helpers/LocaleProvider';
import LazyLoad from '../atoms/LazyLoad';

const ProductCarousel = ({ data: { products } }) => {
  // Sets the current locale .
  const { locale } = useContext(LocaleContext);

  // Query allStrapiProductListing to get slug of product listing page.
  const { allStrapiProductListing } = useStaticQuery(graphql`
    {
      allStrapiProductListing {
        nodes {
          locale
          slug
        }
      }
    }
  `);

  // Find matching locale.
  const strapiProductListing = allStrapiProductListing.nodes.find(
    (node) => node.locale === locale
  );
  const basePath = `${locale}/${strapiProductListing?.slug}`;

  return (
    <Section
      overflow="hidden"
      sx={{
        '.swiper': {
          py: 1, // to prevent overflow hidden cropping focus state on buttons.
          overflow: `visible`
        },
        '.swiper-wrapper': {
          width: `fit-content` // to ensure correct width is set on slider wrapper and dragging works.
        }
      }}>
      <LazyLoad>
        <Wrapper>
          <Swiper
            modules={[Navigation, Pagination]}
            navigation={{
              prevEl: `.prev`,
              nextEl: `.next`
            }}
            pagination={{ el: `.pagination`, clickable: true }}
            spaceBetween={25}
            slidesPerView={1}
            breakpoints={{
              600: {
                slidesPerView: 1.75,
                spaceBetween: 30
              },
              768: {
                slidesPerView: 2.1,
                spaceBetween: 48
              },
              1024: {
                slidesPerView: 2.5,
                spaceBetween: 48
              },
              1200: {
                slidesPerView: 2.6,
                spaceBetween: 48
              }
            }}>
            {products &&
              products.map((product) => (
                <SwiperSlide key={product.title} className="product-hover">
                  <Box
                    as={GatsbyLink}
                    to={`/${basePath}/${product.slug}`}
                    display="block"
                    position="relative">
                    <AspectRatio
                      ratio={1}
                      display="block"
                      transition="transform .5s ease-in-out"
                      sx={{
                        '.product-hover:hover &': {
                          transform: { md: `auto` },
                          scale: { md: `1.1` }
                        }
                      }}>
                      <Image image={product.thumbnail} objectFit="contain" />
                    </AspectRatio>
                    <Heading
                      as="h2"
                      size="3xl"
                      fontFamily="headingSub"
                      letterSpacing="-0.0156rem"
                      color="white"
                      pt="6"
                      textAlign="center"
                      textTransform="uppercase"
                      transition="color .3s ease-in-out"
                      sx={{
                        '.product-hover:hover &': {
                          color: `milka.primary.dark-purple`
                        }
                      }}>
                      {product.title}
                    </Heading>
                  </Box>
                </SwiperSlide>
              ))}
            <HStack
              slot="container-end"
              align={{ md: `center` }}
              justifyContent={{ base: `center`, md: `flex-end` }}
              mt={{ base: 8, md: 16 }}
              spacing={{ md: 8 }}>
              <SliderPagination />
              <Hide below="md">
                <SliderArrows />
              </Hide>
            </HStack>
          </Swiper>
        </Wrapper>
      </LazyLoad>
    </Section>
  );
};

export default ProductCarousel;
